import React from "react";

import { Button } from "antd";
import { toast } from "react-toastify";

import { AppContext } from "../../../../../AppContext";
import StyledScrollbar from "../../../../Common/UIComponents/StyledScrollbar";
import StyledTooltip from "../../../../Common/UIComponents/StyledTooltip";
import ConfirmModal from "./ConfirmModal";
import EditSpecTable from "./EditSpecTable";
import EditSpecTestSpecHeader from "./EditSpecTestSpecHeader";
import EditSpecTestSpecWorkflow from "./EditSpecTestSpecWorkflow";
import {
  computeTestSpecPreviewAdvanceState,
  isSpecEmptyOrNotUpdated,
  UNIT_OPTIONS,
  isCustomSpecProduct,
  globalTestSpecSpecFormBlocksSave,
} from "./Helper";
import SpecTable from "./SpecTable";
import TestSpecForm from "./TestSpecForm";
import UpdateTestList from "./UpdateTestList";

import { hasPermission, permissions } from "../../../../../utils/Permissions";

import { getSpecsUnits } from "../../../../../actions/specManager";

import { ReactComponent as LoadingDots } from "../../../../../assets/images/common/LoadingDots.svg";
import watermelon from "../../../../../assets/images/EmptyState_V2.png";
import backIcon from "../../../../../assets/images/product/backIconDarkBlue.png";
import { ReactComponent as MicroScopeIcon } from "../../../../../assets/images/product/specmanager/microscope-line.svg";

import "../../../Product.css";
import "./SpecManager.css";
import "./EditSpec.css";
import "./EditSpecTestSpec.css";
import "./GlobalTestSpec/TestSpecOverview.css";

class EditSpec extends React.Component {
  constructor(props) {
    super(props);
    const { addingSpec, editingSpec } = this.props;
    this.state = {
      saveConfirmation: false,
      editing: editingSpec || false,
      addingSpecState: addingSpec,
      updateConfirmationLoading: false,
      canAddSpec: hasPermission(permissions.specManager.addSpec),
      unitOptions: UNIT_OPTIONS,
      testSpecAddCountFromAllList: 0,
      testSpecSelectedCount: 0,
      testSpecAllProductsLoading: true,
    };

    this.handleExitModal = this.handleExitModal.bind(this);
    this.handleEditButtonClicked = this.handleEditButtonClicked.bind(this);
  }

  componentDidMount() {
    const { addingSpec, isEditingTestSpec } = this.props;
    const { canAddSpec } = this.state;
    if (addingSpec && (canAddSpec || !isEditingTestSpec)) {
      this.setState({ editing: true });
    }
    this._isMounted = true;
    const controller = new AbortController();
    this.abortController = controller;
    getSpecsUnits(controller.signal).then((res) => {
      if (this._isMounted && res?.success && Array.isArray(res.result) && res.result.length > 0) {
        this.setState({ unitOptions: ["None", ...res.result] });
      }
    });
  }

  componentDidUpdate(prevProps, prevState) {
    const {
      isEditingTestSpec,
      onTestSpecSearchValueChange,
      testSpecSearchValue,
    } = this.props;
    const { editing } = this.state;
    if (
      isEditingTestSpec
      && prevState.editing !== editing
      && typeof onTestSpecSearchValueChange === "function"
      && String(testSpecSearchValue ?? "").length > 0
    ) {
      onTestSpecSearchValueChange("");
    }
  }

  componentWillUnmount() {
    this._isMounted = false;
    const { setShowUpdateTestList } = this.props;
    if (typeof setShowUpdateTestList === "function") {
      setShowUpdateTestList(false);
    }
    if (this.abortController) {
      this.abortController.abort();
    }
  }

  handleClearSpec = (e) => {
    const { dataOfSpecEditing, handleDataChange } = this.props;
    e.preventDefault();
    const specList = dataOfSpecEditing;
    const specListCleared = specList.map((spec) => {
      const cleared = {
        test: spec.test,
        test_type: "",
        specs: [],
      };
      if (spec.is_panel_subtest === true) {
        cleared.is_panel_subtest = true;
      }
      const parentPanel = spec.parent_panel != null ? String(spec.parent_panel).trim() : "";
      if (parentPanel) {
        cleared.parent_panel = parentPanel;
      }
      return cleared;
    });
    handleDataChange(specListCleared);
    // this.props.handleSaveSpec(true);
  }

  handleSaveSpec = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    const {
      handleSaveSpec, handleSpecChanged, fromModal, handleClosingEditSpec, isEditingTestSpec,
    } = this.props;
    const { saveConfirmation } = this.state;

    // Test Spec: only ProductSpecificSpecsModal (global) — no EditSpec ConfirmModal.
    if (!isEditingTestSpec) {
      // First click: show ConfirmModal. Confirm button calls this again.
      if (!saveConfirmation) {
        this.setState({ saveConfirmation: true });
        return;
      }
    }

    this.setState({ updateConfirmationLoading: true });
    try {
      const response = await handleSaveSpec();

      if (response === true) {
        if (fromModal) {
          handleClosingEditSpec();
        } else {
          this.setState({
            editing: false,
            addingSpecState: false,
          });

          handleSpecChanged(false);
        }
      }
    } catch (error) {
      toast.error("Failed to save spec.");
    } finally {
      this.setState({
        saveConfirmation: false,
        updateConfirmationLoading: false,
      });
    }
  }

  handleConfirmationPopup() {
    const { saveConfirmation, addingSpecState, updateConfirmationLoading } = this.state;

    const productSaveBodyText = "Adding specs will update the status of all samples linked to this product. This cannot be undone.";

    if (saveConfirmation && addingSpecState) {
      return (
        <ConfirmModal
          headerText="Add Specs to Product?"
          bodyText={productSaveBodyText}
          buttonText={["Cancel", "Add Specs"]}
          buttonFunctions={[() => { this.setState({ saveConfirmation: false }); }, this.handleSaveSpec]}
          disableConfirmButton={updateConfirmationLoading}
          loadingConfirm={updateConfirmationLoading}
        />
      );
    }

    if (saveConfirmation && !addingSpecState) {
      return (
        <ConfirmModal
          headerText="Update Specs to Product?"
          bodyText={productSaveBodyText}
          buttonText={["Cancel", "Update Specs"]}
          buttonFunctions={[() => { this.setState({ saveConfirmation: false }); }, this.handleSaveSpec]}
          disableConfirmButton={updateConfirmationLoading}
          loadingConfirm={updateConfirmationLoading}
        />
      );
    }

    return <></>;
  }

  handleExitModal() {
    const {
      confirmExitModalDisplayToggle, handleExitEdit, specChanged, valueHasBeenEdited,
    } = this.props;
    const { editing } = this.state;

    if (editing && specChanged) {
      const response = confirmExitModalDisplayToggle();
      this.setState({
        editing: response,
      });
    } else {
      handleExitEdit(false, valueHasBeenEdited);
    }
  }

  handleEditButtonClicked() {
    this.setState({
      editing: true,
    });
  }

  /**
   * Handler for back button click
   */
  handleBackButtonClick = () => {
    const { showUpdateTestList, setShowUpdateTestList, isEditingTestSpec } = this.props;
    const { editing } = this.state;
    if (showUpdateTestList) {
      setShowUpdateTestList(false);
      return;
    }
    if (isEditingTestSpec && !editing) {
      this.setState({ editing: true });
      return;
    }
    this.handleExitModal();
  }

  /**
   * Single source of truth for Step 2 / footer "Preview Selection" gating (validations).
   */
  computeTestSpecPreviewAdvanceState = () => {
    const {
      loading,
      dataOfSpecEditing = [],
      testSpecEditHasDraftChanges = true,
      testSpecHasStep1Baseline = true,
      selectedProductCount,
    } = this.props;
    const {
      testSpecAddCountFromAllList,
      testSpecSelectedCount,
      testSpecAllProductsLoading,
    } = this.state;

    return computeTestSpecPreviewAdvanceState({
      loading,
      dataOfSpecEditing,
      testSpecEditHasDraftChanges,
      testSpecHasStep1Baseline,
      selectedProductCount,
      testSpecAddCountFromAllList,
      testSpecSelectedCount,
      testSpecAllProductsLoading,
    });
  }

  advanceToTestSpecPreviewStepIfAllowed = () => {
    const { previewPrimaryDisabled, isAddProductsMode } = this.computeTestSpecPreviewAdvanceState();
    if (previewPrimaryDisabled) return;
    if (isAddProductsMode) {
      if (typeof this.addCheckedProductsFromAllList === "function") {
        this.addCheckedProductsFromAllList();
      }
    } else {
      this.setState({ editing: false });
    }
  }

  callProperUpdateDataFunction = (specList) => {
    const { handleDataChange, handleSpecChanged, isEditingTestSpec } = this.props;

    handleDataChange(specList);
    if (!isEditingTestSpec) {
      handleSpecChanged(true);
    }
  };

  saveConfirmation(value) {
    this.setState({
      saveConfirmation: value,
    });

    return value;
  }

  renderSecondaryButton() {
    const { isEditingTestSpec, setShowUpdateTestList } = this.props;
    if (isEditingTestSpec) {
      return (
        <Button
          type="default"
          onClick={this.handleBackButtonClick}
        >
          Cancel
        </Button>
      );
    }
    return (
      <Button
        type="primary"
        ghost
        onClick={() => setShowUpdateTestList(true)}
      >
        Update Test List
      </Button>
    );
  }

  renderButtons() {
    const {
      editing,
      addingSpecState,
    } = this.state;
    const {
      dataOfSpecEditing, isEditingTestSpec, selectedProductCount = 0,
    } = this.props;

    const allowEmpty = !isEditingTestSpec;
    const disableButton = dataOfSpecEditing.length > 0 && (
      isEditingTestSpec
        ? globalTestSpecSpecFormBlocksSave(dataOfSpecEditing)
        : isSpecEmptyOrNotUpdated(dataOfSpecEditing, allowEmpty)
    );
    const isProductListEmpty = isEditingTestSpec && selectedProductCount === 0;
    const isDisabled = disableButton || isProductListEmpty;
    let title = "";
    if (disableButton) {
      title = "Missing or invalid inputs";
    } else if (isProductListEmpty) {
      title = "Please select at least one product";
    }

    if (addingSpecState && dataOfSpecEditing.length) {
      const primaryButtonText = isEditingTestSpec ? "Save Spec" : "Add Specs";
      const shouldShowPreviewFollowsBadge = isEditingTestSpec && !editing;
      return (
        <>
          {shouldShowPreviewFollowsBadge ? (
            <div className="EditSpec__TestSpecPreviewFollowsBadge">
              <MicroScopeIcon className="EditSpec__TestSpecPreviewFollowsBadgeIcon" />
              {selectedProductCount}
              {" "}
              {selectedProductCount === 1 ? "product" : "products"}
              {" "}
              selected
            </div>
          ) : (
            <div />
          )}
          <div className="EditSpec__Button_Container">
            {this.renderSecondaryButton()}
            <StyledTooltip
              title={title}
              placement="top"
            >
              <Button
                type="primary"
                disabled={isDisabled}
                onClick={!isDisabled ? this.handleSaveSpec : null}
              >
                {primaryButtonText}
              </Button>
            </StyledTooltip>
          </div>
        </>
      );
    }

    // Legacy "Edit" button
    // because the new Test Spec View mode is handled outside this component.
    if (!editing && dataOfSpecEditing.length && !isEditingTestSpec) {
      const { disableEditForMRL = false } = this.props;
      return (
        <>
          <div />
          <StyledTooltip
            title={disableEditForMRL ? "Editing is disabled for MRL tests" : ""}
            placement="top"
          >
            <Button
              type="primary"
              ghost
              disabled={disableEditForMRL}
              onClick={!disableEditForMRL ? this.handleEditButtonClicked : null}
            >
              Edit
            </Button>
          </StyledTooltip>
        </>
      );
    }
    if (dataOfSpecEditing.length) {
      const shouldShowPreviewFollowsBadge = isEditingTestSpec && !editing;
      return (
        <>
          {shouldShowPreviewFollowsBadge ? (
            <div className="EditSpec__TestSpecPreviewFollowsBadge">
              <MicroScopeIcon className="EditSpec__TestSpecPreviewFollowsBadgeIcon" />
              <strong>
                {selectedProductCount}
                {" "}
                {selectedProductCount === 1 ? "product" : "products"}
                {" "}
              </strong>
              selected
            </div>
          ) : (
            <div />
          )}
          <div className="EditSpec__Button_Container">
            {this.renderSecondaryButton()}
            <StyledTooltip
              title={title}
              placement="top"
            >
              <Button
                type="primary"
                disabled={isDisabled}
                onClick={!isDisabled ? this.handleSaveSpec : null}
              >
                Update Specs
              </Button>
            </StyledTooltip>
          </div>
        </>
      );
    }
    return null;
  }

  renderTable() {
    const {
      editing, addingSpecState, canAddSpec, unitOptions,
    } = this.state;
    const {
      addingSpec, dataOfSpecEditing, loading, isEditingTestSpec, suggestedSpecTests,
    } = this.props;

    if (!loading) {
      if (!dataOfSpecEditing.length) {
        return (
          <div className="productReportEditSpecNoResult">
            <img
              className="productReportEditSpecNoResultWatermelonImg"
              alt="no result"
              src={watermelon}
            />
            <div className="productReportEditSpecNoResultErrorMessage">
              No Tests Found
            </div>
          </div>
        );
      }
      // For Test Spec, always render the editable table when user has permission.
      if (canAddSpec && (editing || addingSpecState || isEditingTestSpec)) {
        if (isEditingTestSpec) {
          return (
            <TestSpecForm
              dataOfSpecEditing={dataOfSpecEditing}
              handleDataChange={this.callProperUpdateDataFunction}
              unitOptions={unitOptions}
            />
          );
        }

        return (
          <EditSpecTable
            addingSpec={addingSpec}
            dataOfSpecEditing={dataOfSpecEditing}
            handleDataChange={this.callProperUpdateDataFunction}
            unitOptions={unitOptions}
            suggestedSpecTests={suggestedSpecTests}
          />
        );
      }

      return (
        <SpecTable
          addingSpec={addingSpec}
          dataOfSpecEditing={dataOfSpecEditing}
          handleDataChange={this.callProperUpdateDataFunction}
          prioritizeMRLWhenCountriesPresent={isEditingTestSpec}
        />
      );
    }
    return (
      <div className="productReportEditSpecLoading">
        <LoadingDots width={28} height={28} fill="#19305A" />
      </div>
    );
  }

  renderProductTitle() {
    const { productIdentifierArr, isEditingTestSpec } = this.props;
    if (isEditingTestSpec) {
      const entries = Object.entries(productIdentifierArr);
      if (entries.length === 1) {
        const [key, value] = entries[0];
        if (String(key) === "Test name") return String(value ?? "");
      }
    }
    let title = "";
    Object.entries(productIdentifierArr).forEach(
      ([key, value], index) => {
        if (value !== "") {
          title += `${index > 0 ? " \u2022 " : ""}${key}: ${value}`;
        }
      },
    );
    return title;
  }

  render() {
    const {
      fromModal, productIdentifierArr, showUpdateTestList, setShowUpdateTestList,
      dataOfSpecEditing, handleDataChange, handleSaveTestList,
      isEditingTestSpec,
      renderProductSelectionSection,
      loading,
      testSpecPreviewSelectedProducts = [],
      testSpecPreviewFields = [],
      testSpecPreviewLinkReportJsonFields = [],
      testSpecSearchValue,
      onTestSpecSearchValueChange,
    } = this.props;

    /*
     * Test Spec: parent renders the product picker and receives footer/add callbacks explicitly
     */
    const productSectionWithFooter = isEditingTestSpec
      && typeof renderProductSelectionSection === "function"
      ? renderProductSelectionSection({
        onFooterStateChange: (payload) => {
          const {
            addCountFromAllList = 0,
            selectedCount = 0,
            allProductsLoading = false,
          } = payload ?? {};
          this.setState({
            testSpecAddCountFromAllList: addCountFromAllList,
            testSpecSelectedCount: selectedCount,
            testSpecAllProductsLoading: allProductsLoading,
          });
        },
        onRegisterAddProductsAction: (action) => {
          this.addCheckedProductsFromAllList = typeof action === "function" ? action : undefined;
        },
      })
      : null;
    const {
      canAddSpec,
      editing,
      testSpecAddCountFromAllList,
    } = this.state;

    const testSpecStandardFollowCount = isEditingTestSpec
      ? Math.max(
        testSpecPreviewSelectedProducts.length
          - testSpecPreviewSelectedProducts.filter(isCustomSpecProduct).length,
        0,
      )
      : 0;

    const {
      previewPrimaryDisabled,
      previewSelectionTooltip,
      previewFooterLoading,
    } = isEditingTestSpec
      ? this.computeTestSpecPreviewAdvanceState()
      : {
        previewPrimaryDisabled: false,
        previewSelectionTooltip: "",
        previewFooterLoading: false,
      };

    return (
      <div className="productReportEditSpecContainer">
        {isEditingTestSpec ? (
          <EditSpecTestSpecHeader
            fromModal={fromModal}
            productTitle={this.renderProductTitle()}
            testSpecSearchValue={testSpecSearchValue}
            onTestSpecSearchValueChange={onTestSpecSearchValueChange}
            editing={editing}
            previewPrimaryDisabled={previewPrimaryDisabled}
            previewFooterLoading={previewFooterLoading}
            onBackButtonClick={this.handleBackButtonClick}
            onAdvancePreviewStep={this.advanceToTestSpecPreviewStepIfAllowed}
          />
        ) : (
          <div className={`productReportEditSpecTitleDiv ${showUpdateTestList ? "productReportEditSpecTitleDiv__UpdateTestList" : ""}`}>
            {!fromModal && (
              <img
                className="productReportEditSpecBackIcon"
                src={backIcon}
                alt="back icon"
                onClick={this.handleBackButtonClick}
              />
            )}
            <StyledScrollbar className="EditSpec__LinkedFieldScrollBar">
              <span className="LinkedFieldsSpan productReportEditSpecTitle" title={this.renderProductTitle()}>
                {Object.entries(productIdentifierArr)
                  .filter(([, value]) => value !== "")
                  .map(([key, value], index, array) => (
                    <React.Fragment key={key}>
                      <span className="LinkedFieldTitle">{`${key}: `}</span>
                      <span className="LinkedFieldValue">{value}</span>
                      {index < array.length - 1 && <span className="LinkedFieldsDelimiterDot" />}
                    </React.Fragment>
                  ))}
              </span>
            </StyledScrollbar>
          </div>
        )}
        {!showUpdateTestList ? (
          <>
            {isEditingTestSpec ? (
              <EditSpecTestSpecWorkflow
                loading={loading}
                editing={editing}
                dataOfSpecEditing={dataOfSpecEditing}
                testSpecPreviewSelectedProducts={testSpecPreviewSelectedProducts}
                testSpecPreviewFields={testSpecPreviewFields}
                testSpecPreviewLinkReportJsonFields={testSpecPreviewLinkReportJsonFields}
                testSpecSearchValue={testSpecSearchValue}
                productSectionWithFooter={productSectionWithFooter}
                productTitle={this.renderProductTitle()}
                renderTable={() => this.renderTable()}
                previewPrimaryDisabled={previewPrimaryDisabled}
                previewSelectionTooltip={previewSelectionTooltip}
                previewFooterLoading={previewFooterLoading}
                testSpecAddCountFromAllList={testSpecAddCountFromAllList}
                testSpecStandardFollowCount={testSpecStandardFollowCount}
                onAdvancePreviewStep={this.advanceToTestSpecPreviewStepIfAllowed}
                onBackFooter={this.handleBackButtonClick}
                canAddSpec={canAddSpec}
                renderButtons={() => this.renderButtons()}
              />
            ) : (
              <>
                <div className="productReportEditSpecTableContainer">
                  {this.renderTable()}
                  {this.handleConfirmationPopup()}
                </div>
                <div
                  id="productReportEditSpecButtons"
                  className="productReportEditSpecButtonsSection"
                >
                  {canAddSpec ? this.renderButtons() : null}
                </div>
              </>
            )}
          </>
        ) : (
          <UpdateTestList
            setShowUpdateTestList={setShowUpdateTestList}
            dataOfSpecEditing={dataOfSpecEditing}
            handleDataChange={handleDataChange}
            handleSaveTestList={handleSaveTestList}
          />
        )}
      </div>
    );
  }
}

EditSpec.contextType = AppContext; // allows class component to consume context, access via this.context
export default EditSpec;
